import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import api from "../connector/api";
import ReturnModal from "./components/ReturnModal";
import { CustomSelect } from "./components/common/CustomSelect";
import { Sidebar } from "./components/common/Sidebar";
import { Header } from "./components/common/Header";
import AppLayout from "./components/common/AppLagout";

// Icons
import {
  BookOpen,
  Users,
  ArrowLeftRight,
  Bookmark,
  Receipt,
  BarChart3,
  GitBranch,
  Settings,
  Library,
  Menu,
  Search,
  Plus,
  Bell,
  Clock,
  Loader2,
  AlertTriangle,
  Download,
  CheckSquare,
  Square,
  SlidersHorizontal,
  Trash2,
  CheckCircle,
} from "lucide-react";

const CATEGORY_COLORS = [
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#f97316",
  "#06b6d4",
];

export default function LibraryDashboard() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [totals, setTotals] = useState({
    total_books: 0,
    available_books: 0,
    borrowed_books: 0,
    overdue_books: 0,
  });

  const [returnLoan, setReturnLoan] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [byCategory, setByCategory] = useState([]);
  const [borrowedList, setBorrowedList] = useState([]);
  const [recentBooks, setRecentBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Ledger search input state
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    const handler = setTimeout(async () => {
      try {
        const res = await api.get("/cataloging/dashboard/search", {
          params: { q: searchTerm },
        });
        const data = res.data || [];
        const normalizedResults = data.map((b) => ({
          ...b,
          cover_url: b.cover_image?.startsWith("http")
            ? b.cover_image
            : b.cover_image
            ? `${api.defaults.baseURL.replace("/api", "")}${b.cover_image}`
            : null,
          is_available: b.status === "available",
        }));
        setSearchResults(normalizedResults);
        setShowDropdown(true);
      } catch (err) {
        console.error("Catalog typeahead sequence dropped:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDashboard = async (isInitial = false) => {
    if (isInitial) setLoading(true); // Only show full-page skeleton on first load
    
    try {
      const res = await api.get("/cataloging/dashboard/", {
        params: {
          search: debouncedSearch || undefined,
          category: activeCategory || undefined,
        },
      });

      setTotals(
        res.data.totals || {
          total_books: 0,
          available_books: 0,
          borrowed_books: 0,
          overdue_books: 0,
        }
      );
      setByCategory(Array.isArray(res.data.by_category) ? res.data.by_category : []);
      setBorrowedList(res.data.borrowed_list || []);

      const rawRecent = Array.isArray(res.data.recent_books) ? res.data.recent_books : [];
      const normalizedRecent = rawRecent.map((b) => ({
        ...b,
        cover_url: b.cover_image?.startsWith("http")
          ? b.cover_image
          : b.cover_image
          ? `${api.defaults.baseURL.replace("/api", "")}${b.cover_image}`
          : null,
      }));

      setRecentBooks(normalizedRecent);
    } catch (err) {
      console.error("Dashboard engine failed to load dataset components:", err);
      toast.error("Failed to fetch dashboard structural components");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDashboard(true);
  }, []);

  // Background refetch on filter/search change without resetting full page UI
  useEffect(() => {
    fetchDashboard(false);
  }, [debouncedSearch, activeCategory]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectBook = (book) => {
    setShowDropdown(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
    navigate(`/library/cataloging/book/detail/${book.id}`);
  };

  const [exportScope, setExportScope] = useState("all_active");
  const [exportLimit, setExportLimit] = useState(0);
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);

  const getFilteredLoans = () => {
    return borrowedList.filter((item) => {
      if (exportScope === "overdue_only") return item.is_overdue;
      return true;
    });
  };

  useEffect(() => {
    const filtered = getFilteredLoans();
    setSelectedLoanIds(filtered.map((item) => item.id));
  }, [exportScope, borrowedList]);

  const filteredLoans = getFilteredLoans();

  const searchFilteredLoans = filteredLoans.filter((item) => {
    const query = searchQuery.toLowerCase();
    const borrowerMatch = item.borrower?.toLowerCase().includes(query);
    const titleMatch = (item.book?.title || item.title || "").toLowerCase().includes(query);
    return borrowerMatch || titleMatch;
  });

  const allVisibleSelected =
    searchFilteredLoans.length > 0 &&
    searchFilteredLoans.every((item) => selectedLoanIds.includes(item.id));

  const handleMasterCheckboxToggle = () => {
    if (allVisibleSelected) {
      const visibleIds = searchFilteredLoans.map((item) => item.id);
      setSelectedLoanIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      const visibleIds = searchFilteredLoans.map((item) => item.id);
      setSelectedLoanIds((prev) => {
        const uniqueIds = new Set([...prev, ...visibleIds]);
        return Array.from(uniqueIds);
      });
    }
  };

  const handleRowCheckboxToggle = (id) => {
    setSelectedLoanIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleExportTrigger = async () => {
    let targetedItems = borrowedList.filter((item) => selectedLoanIds.includes(item.id));

    if (exportScope === "overdue_only") {
      targetedItems = targetedItems.filter((item) => item.is_overdue);
    }

    if (targetedItems.length === 0) {
      return toast.error("No loans selected. Please select at least one row via checkboxes.");
    }

    const sliceLimit = exportLimit === 0 ? targetedItems.length : exportLimit;
    const finalPayload = targetedItems.slice(0, sliceLimit);

    try {
      toast.loading("Generating PDF manifest...");

      const response = await api.post(
        "/loans/export-pdf/",
        {
          loan_ids: finalPayload.map((item) => item.id),
        },
        { responseType: "blob" }
      );

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const fileURL = window.URL.createObjectURL(fileBlob);
      const downloadLink = document.createElement("a");
      downloadLink.href = fileURL;
      downloadLink.setAttribute(
        "download",
        `Circulation_Manifest_${new Date().toISOString().split("T")[0]}.pdf`
      );
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();

      toast.dismiss();
      toast.success("PDF exported successfully");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to compile manifest report file.");
    }
  };

  const exportScopeOptions = [
    { value: "all_active", label: "All Active Transactions" },
    { value: "overdue_only", label: "Overdue Only" },
  ];

  const exportLimitOptions = [
    { value: 0, label: "Export All Checked" },
    { value: 10, label: "Top 10 Checked" },
    { value: 25, label: "Top 25 Checked" },
    { value: 50, label: "Top 50 Checked" },
  ];

  return (
    <AppLayout
      headerProps={{
        title: "Dashboard",
        searchTerm,
        setSearchTerm,
        searchRef,
        searchLoading,
        searchResults,
        showDropdown,
        setShowDropdown,
        highlightedIndex,
        setHighlightedIndex,
        handleSelectBook,
      }}
    >
      <Toaster position="top-right" />

      {/* Dashboard Content */}
      <div className="space-y-6 pb-6">
        {loading ? (
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 bg-[#1e1a16] rounded-2xl" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="h-64 bg-[#1e1a16] rounded-2xl lg:col-span-2" />
              <div className="h-64 bg-[#1e1a16] rounded-2xl" />
            </div>
          </div>
        ) : (
          <>
            {/* Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-[#191613] p-4 rounded-2xl border border-[#26201a] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20 text-amber-400">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                    Active
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-black text-stone-100">{totals?.total_books || 0}</h3>
                  <p className="text-xs text-stone-500 font-medium">Titles in collection</p>
                </div>
              </div>

              <div className="bg-[#191613] p-4 rounded-2xl border border-[#26201a] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-emerald-400">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                    Ready
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-black text-stone-100">{totals?.available_books || 0}</h3>
                  <p className="text-xs text-stone-500 font-medium">Available in stock</p>
                </div>
              </div>

              <div className="bg-[#191613] p-4 rounded-2xl border border-[#26201a] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-sky-500/10 rounded-xl border border-sky-500/20 text-sky-400">
                    <ArrowLeftRight className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full border border-sky-500/20">
                    On Loan
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-black text-stone-100">{totals?.borrowed_books || 0}</h3>
                  <p className="text-xs text-stone-500 font-medium">Books on loan</p>
                </div>
              </div>

              <div className="bg-[#191613] p-4 rounded-2xl border border-[#26201a] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-rose-500/10 rounded-xl border border-rose-500/20 text-rose-400">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/20">
                    Action
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-black text-rose-400">{totals?.overdue_books || 0}</h3>
                  <p className="text-xs text-stone-500 font-medium">Overdue books</p>
                </div>
              </div>
            </div>

            {/* Middle Grid: Active Borrowing Ledger & Genre Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-[#191613] border border-[#26201a] rounded-2xl p-5 space-y-4 flex flex-col justify-between">
                <div>
                  {/* Header & Controls */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#231e18] pb-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleMasterCheckboxToggle}
                        className="text-stone-500 hover:text-stone-300"
                      >
                        {allVisibleSelected ? (
                          <CheckSquare className="w-4 h-4 text-amber-400" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                      <h3 className="text-xs font-bold text-stone-200 uppercase tracking-wider">
                        Active Borrowing Ledger
                      </h3>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Search Input Bar */}
                      <div className="relative flex-1 sm:w-48">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500" />
                        <input
                          type="text"
                          placeholder="Search patron or book..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-[#181512] border border-[#2e2720] rounded-lg pl-8 pr-3 py-1 text-xs text-stone-200 placeholder:text-stone-500 focus:outline-none focus:border-amber-500/50 transition"
                        />
                      </div>

                      {/* Counter Badge */}
                      <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-1 rounded-md text-xs font-mono shrink-0">
                        {selectedLoanIds.length} / {searchFilteredLoans.length} Selected
                      </span>
                    </div>
                  </div>

                  {/* Loans List */}
                  <div className="mt-4 max-h-60 overflow-y-auto space-y-2 pr-1 divide-y divide-[#231e18] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500">
                    {searchFilteredLoans.length === 0 ? (
                      <div className="p-6 text-center text-xs text-stone-500">
                        No items matching active filter or search criteria
                      </div>
                    ) : (
                      searchFilteredLoans.map((item) => {
                        const bookData = item.book || { title: item.title || "Unknown Holding" };
                        const isChecked = selectedLoanIds.includes(item.id);

                        return (
                          <div
                            key={item.id}
                            className={`pt-2.5 first:pt-0 flex items-center justify-between gap-4 p-2 rounded-xl transition ${
                              isChecked ? "bg-[#211d18]" : ""
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <button
                                type="button"
                                onClick={() => handleRowCheckboxToggle(item.id)}
                                className="text-stone-500 hover:text-stone-300 shrink-0"
                              >
                                {isChecked ? (
                                  <CheckSquare className="w-4 h-4 text-amber-400" />
                                ) : (
                                  <Square className="w-4 h-4" />
                                )}
                              </button>
                              <div className="min-w-0">
                                <p className="text-xs font-serif font-bold tracking-wider text-stone-200 truncate">
                                  {bookData.title}
                                </p>
                                <p className="text-[10px] text-stone-500 truncate mt-0.5">
                                  {item.borrower}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-right text-[10px] font-mono hidden sm:block">
                                <p
                                  className={
                                    item.is_overdue
                                      ? "text-rose-400 font-semibold"
                                      : item.is_due_today
                                      ? "text-amber-400 font-semibold"
                                      : "text-stone-400"
                                  }
                                >
                                  Due: {new Date(item.due_date).toLocaleDateString()}
                                </p>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  setReturnLoan({
                                    loanId: item.id,
                                    book: bookData,
                                    is_overdue: item.is_overdue,
                                  });
                                  setReturnOpen(true);
                                }}
                                className="p-1.5 text-stone-400 bg-[#231e18] border border-[#2e2720] rounded-lg hover:text-amber-400 hover:border-amber-500/30 transition"
                                title="Process Return Check-In"
                              >
                                <ArrowLeftRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Fixed Single Sliders Control */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-[#201c17] p-3 rounded-xl border border-[#2a241e] mt-4">
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <SlidersHorizontal className="w-4 h-4 text-stone-500 shrink-0" />
                    <CustomSelect
                      value={exportScope}
                      onChange={(val) => setExportScope(val)}
                      options={exportScopeOptions}
                    />
                    <CustomSelect
                      value={exportLimit}
                      onChange={(val) => setExportLimit(Number(val))}
                      options={exportLimitOptions}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleExportTrigger}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-stone-950 text-xs font-bold rounded-lg transition"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>

              {/* Genre Breakdown Card */}
              <div className="bg-[#191613] border border-[#26201a] rounded-2xl p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-bold text-stone-200">Collection by Genre</h3>
                      <p className="text-[11px] text-stone-500">Distribution by category</p>
                    </div>
                    {activeCategory && (
                      <button
                        type="button"
                        onClick={() => setActiveCategory(null)}
                        className="text-[10px] text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Clear</span>
                      </button>
                    )}
                  </div>

                  <div className="space-y-3.5 max-h-64 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500">
                    {byCategory.length === 0 ? (
                      <div className="text-center py-8 text-xs text-stone-500">
                        No category distribution recorded
                      </div>
                    ) : (
                      (() => {
                        const totalVolumes = byCategory.reduce((sum, item) => sum + item.count, 0);

                        return byCategory.map((cat, idx) => {
                          const isSelected = activeCategory === cat.category;
                          const percentage = totalVolumes > 0 ? ((cat.count / totalVolumes) * 100).toFixed(1) : 0;
                          const color = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];

                          return (
                            <div
                              key={cat.category || idx}
                              onClick={() => setActiveCategory(isSelected ? null : cat.category)}
                              className={`p-2 rounded-xl transition cursor-pointer ${
                                isSelected ? "bg-[#25201a] border border-amber-500/30" : "hover:bg-[#201c17]"
                              }`}
                            >
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="w-2.5 h-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-stone-300 font-medium font-serif">{cat.category}</span>
                                </div>
                                <span className="font-mono text-stone-400 text-[11px]">
                                  {cat.count} ({percentage}%)
                                </span>
                              </div>
                              <div className="w-full bg-[#201c17] h-1.5 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-300"
                                  style={{ width: `${percentage}%`, backgroundColor: color }}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Newly Registered Acquisitions Shelf */}
            <div className="bg-[#191613] border border-[#26201a] rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-[#231e18] pb-3">
                <h3 className="text-sm font-bold text-stone-200 uppercase tracking-wider">
                  Newly Registered Catalog Acquisitions
                </h3>
                <CheckCircle className="w-4 h-4 text-stone-500" />
              </div>

              {recentBooks.length === 0 ? (
                <p className="text-stone-500 text-xs py-6 text-center font-medium">
                  No recent acquisitions found
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-72 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500">
                  {recentBooks.map((b) => (
                    <div
                      key={b.id}
                      onClick={() => navigate(`/library/cataloging/book/detail/${b.id}`)}
                      className="flex gap-3.5 items-center p-3 rounded-xl bg-[#201c17] border border-[#2a241e] hover:border-amber-500/30 cursor-pointer transition group"
                    >
                      <div className="w-10 h-14 bg-[#161310] rounded-lg overflow-hidden shrink-0 flex items-center justify-center border border-[#2e2720]">
                        {b.cover_url ? (
                          <img
                            src={b.cover_url}
                            alt={b.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition"
                          />
                        ) : (
                          <BookOpen className="w-4 h-4 text-stone-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold font-serif tracking-wider text-xs text-stone-200 truncate group-hover:text-amber-400 transition">
                          {b.title}
                        </div>
                        <div className="text-[11px] text-stone-500 truncate mt-0.5">
                          {(b.authors || []).join(", ") || "Unspecified Author"}
                        </div>
                        <div className="inline-block text-[9px] bg-[#28221c] text-amber-400 font-bold px-2 py-0.5 rounded mt-1.5 border border-amber-500/10">
                          {b.category || "General"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Return Modal Overlay */}
      <ReturnModal
        open={returnOpen}
        loan={returnLoan}
        onClose={() => setReturnOpen(false)}
        onReturnSuccess={async (loan) => {
          try {
            await api.post(`/loans/${loan.loanId}/return/`);
            setBorrowedList((prev) => prev.filter((l) => l.id !== loan.loanId));
            setTotals((prev) => ({
              ...prev,
              borrowed_books: Math.max(0, prev.borrowed_books - 1),
              overdue_books: Math.max(0, prev.overdue_books - (loan.is_overdue ? 1 : 0)),
            }));
            toast.success("Book item return transaction processed successfully");
            setReturnOpen(false);
          } catch (err) {
            console.error("Return modification trace failure:", err);
            toast.error("Failed to update media return entry registries");
          }
        }}
      />
    </AppLayout>
  );
}